import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatRupiah } from "@/lib/format";
import { Check, Crown, Wallet, Info, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Paywall() {
  const navigate = useNavigate();
  const { user, isPremium, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [creating, setCreating] = useState(null);
  const [paid, setPaid] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.post("/meta/track", { event: "paywall_viewed" }).catch(() => {});
    api.post("/meta/track", { event: "regular_pricing_viewed" }).catch(() => {});
    api.get("/meta/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const startCheckout = async (planId) => {
    setCreating(planId);
    api.post("/meta/track", { event: "plan_selected", meta: { plan_id: planId } }).catch(() => {});
    const planEvent = { plan_30: "plan_30_clicked", plan_90: "plan_90_clicked", plan_365: "plan_365_clicked" }[planId];
    if (planEvent) api.post("/meta/track", { event: planEvent, meta: { plan_id: planId } }).catch(() => {});
    try {
      const { data } = await api.post("/payments/doku/checkout", { plan_id: planId });
      if (!data.payment_url) { toast.error("Pembayaran belum bisa dibuat. Silakan coba lagi nanti."); return; }
      api.post("/meta/track", { event: "checkout_started", meta: { plan_id: planId } }).catch(() => {});
      window.location.assign(data.payment_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCreating(null); }
  };

  const pollStatus = async (orderId) => {
    try {
      const { data } = await api.get(`/payments/${orderId}`);
      if (["settlement", "capture"].includes(data.status) || data.paid) {
        clearInterval(pollRef.current);
        setPaid(true);
        await checkAuth();
        toast.success("Pembayaran berhasil! Premium Anda aktif.");
        setTimeout(() => { setCheckout(null); navigate("/dashboard"); }, 2200);
      } else if (["expire", "cancel", "deny"].includes(data.status)) {
        clearInterval(pollRef.current);
        toast.error("Pembayaran gagal atau kedaluwarsa. Coba lagi.");
        setCheckout(null);
      }
    } catch { /* keep polling */ }
  };

  const closeDialog = () => { clearInterval(pollRef.current); setCheckout(null); };

  const benefits = ["Bandingkan sampai Anda yakin", "Hitung biaya kepemilikan (TCO)", "Cek kecocokan rute & tanjakan", "Simpan kandidat ke Pilihan Saya", "Unduh ringkasan keputusan (PDF)"];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="paywall-page">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4"><Crown className="w-8 h-8 text-amber-500" /></div>
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy">Lanjutkan Analisis Sampai Anda Yakin</h1>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto" data-testid="paywall-message">Gunakan semua alat analisis selama masa akses untuk membandingkan kandidat sebelum membeli.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto items-start">
        {plans.map((p) => {
          const highlight = !!p.label;
          const secondary = p.plan_id === "plan_365";
          const perDay = Math.max(1, Math.round(p.price / p.duration_days));
          return (
            <div key={p.plan_id} data-testid={`paywall-plan-${p.plan_id}`} className={`relative rounded-3xl p-7 border-2 bg-white ${highlight ? "border-teal shadow-xl lg:scale-105" : "border-border"} ${secondary ? "opacity-95" : ""}`}>
              {p.label && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal text-white text-xs font-bold px-4 py-1 rounded-full">{p.label}</span>}
              <h3 className="font-heading font-bold text-lg text-navy">{p.name}</h3>
              <p className="font-heading font-extrabold text-3xl text-navy mt-2">{formatRupiah(p.price)}</p>
              <p className="text-xs text-muted-foreground mb-1">≈ {formatRupiah(perDay)}/hari · akses {p.duration_days} hari</p>
              {p.plan_id === "plan_90" && <p className="text-xs text-teal font-semibold mb-3">Hemat Rp10.000 dibanding 3× paket 30 hari.</p>}
              {secondary && <p className="text-xs text-muted-foreground mb-3">Untuk pemakaian jangka panjang.</p>}
              {!secondary && (
                <ul className="space-y-2 my-4">
                  {benefits.map((b) => <li key={b} className="flex items-start gap-2 text-sm text-navy"><Check className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />{b}</li>)}
                </ul>
              )}
              <Button data-testid={`paywall-cta-${p.plan_id}`} disabled={creating === p.plan_id} onClick={() => startCheckout(p.plan_id)} className={`w-full rounded-full h-12 mt-2 ${highlight ? "bg-teal hover:bg-teal-hover" : "bg-navy hover:bg-navy/90"}`}>
                {creating === p.plan_id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Aktifkan ${p.name} — ${formatRupiah(p.price)}`}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-8 max-w-3xl mx-auto bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="paywall-payment-info">
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Pembayaran aman diproses oleh DOKU.</li>
          <li>• Tidak ada perpanjangan otomatis.</li>
          <li>• Akses aktif setelah pembayaran terkonfirmasi.</li>
        </ul>
      </div>

      <div className="text-center mt-8">
        <button onClick={() => navigate(-1)} className="text-muted-foreground text-sm hover:text-navy" data-testid="paywall-back">Kembali</button>
      </div>

      <Dialog open={!!checkout} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-white max-w-sm" data-testid="checkout-dialog">
          <DialogHeader><DialogTitle>{paid ? "Pembayaran Berhasil" : "Scan QRIS untuk Membayar"}</DialogTitle></DialogHeader>
          {paid ? (
            <div className="text-center py-8" data-testid="checkout-success">
              <CheckCircle2 className="w-16 h-16 text-status-green mx-auto mb-4" />
              <p className="font-semibold text-navy">Premium Anda telah aktif!</p>
              <p className="text-sm text-muted-foreground">Mengalihkan ke dashboard…</p>
            </div>
          ) : checkout ? (
            <div className="text-center">
              <p className="font-heading font-extrabold text-2xl text-navy mb-1">{formatRupiah(checkout.amount)}</p>
              <p className="text-sm text-muted-foreground mb-4">Premium {checkout.plan_name}</p>
              {checkout.qr_url ? (
                <img src={checkout.qr_url} alt="QRIS" className="w-56 h-56 object-contain mx-auto border rounded-xl p-2" data-testid="checkout-qr" />
              ) : (
                <p className="text-sm text-red-600">QR tidak tersedia. Coba lagi.</p>
              )}
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Menunggu pembayaran…
              </div>
              <p className="text-xs text-muted-foreground mt-3">Scan dengan aplikasi apa pun berlogo QRIS (GoPay, OVO, DANA, m-banking). Halaman ini akan otomatis diperbarui.</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
